import React, { useMemo } from "react";
import Grid from "@mui/material/Grid";
import { FormControl, Modal, Skeleton } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { Select, DatePicker, ConfigProvider, theme as antdTheme } from "antd";
import { useAxiosPrivate } from "hooks/useAxiosPrivate";
import { getLoggedInUser } from "service/AuthService";
import { HTTP_STATUS, HTTP_STATUS_MESSAGE } from "constants/HttpStatus";
import { useMaterialUIController } from "context";
import { useNavigate, useSearchParams } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";
import reportsBarChartData from "layouts/dashboard/data/reportsBarChartData";
import reportsLineChartData from "layouts/dashboard/data/reportsLineChartData";
import MDTypography from "components/MDTypography";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import Purpose from "./components/charts/Purpose";
import { error } from "ajv/dist/vocabularies/applicator/dependencies";
// import LeadDetailsModal from "layouts/activity/LeadDetailsModal";
import LeadActivites from "layouts/leads/LeadActivites";
const dashboard = () => {
  const { sales, tasks } = reportsLineChartData;
  const { RangePicker } = DatePicker;
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const [teamEmployeeList, setTeamEmployeeList] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState({});
  const [teamMember, setTeamMember] = useState(null);
  const [selectionSource, setSelectionSource] = useState("init");
  // "init" | "reset" | "dropdown"

  const [leadsData, setLeadsData] = useState([]);
  const [activityData, setActivityData] = useState([]);
  const [dates, setDates] = useState(null);
  const [apiParams, setApiParams] = useState({
    startTime: dayjs().startOf("day").unix(),
    endTime: dayjs().endOf("day").unix(),
    forTeams: "",
    employeeId: "",
  });
  const [loading, setLoading] = useState(false);

  /* -------------------- ICON & COLOR MAP -------------------- */

  const iconMapLead = {
    Cold: "ac_unit",
    Hot: "whatshot",
    Warm: "wb_sunny",
    Marketing: "campaign",
    Onhold: "pause_circle",
    Win: "emoji_events",
  };

  const colorMapLead = {
    Cold: "info",
    Hot: "error",
    Warm: "warning",
    Marketing: "secondary",
    Onhold: "dark",
    Win: "success",
  };

  const iconMapTask = {
    callDetails: "call",
    followUp: "follow_the_signs",
    meeting: "groups",
    overdue: "alarm_on",
    siteVisit: "location_on",
  };

  const colorMapTask = {
    callDetails: "primary",
    followUp: "info",
    meeting: "warning",
    overdue: "error",
    siteVisit: "success",
  };

  const convertLeadData = (leadObj = {}) =>
    Object.entries(leadObj).map(([key, value]) => ({
      title: key,
      count: value,
      icon: iconMapLead[key],
      color: colorMapLead[key],
    }));

  const convertTaskData = (taskObj = {}) =>
    Object.entries(taskObj).map(([key, value]) => ({
      title: value.displayName,
      count: value.total,
      done: value.done,
      icon: iconMapTask[key],
      color: colorMapTask[key],
    }));

  const calculateForTeams = ({ teamEmployee, employeeId, selectionSource }) => {
    if (!teamEmployee || Object.keys(teamEmployee).length <= 1) return false;
    const isSelf = teamEmployee[employeeId]?.self === true;
    if ((selectionSource === "init" || selectionSource === "reset") && isSelf) {
      return true;
    }
    if (selectionSource === "dropdown" && isSelf) {
      return false;
    }
    return true;
  };

  const handleTeamMemberChange = (employeeId) => {
    const startTime = apiParams.startTime;
    const endTime = apiParams.endTime;
    setSelectedEmployee({ ...teamEmployeeList[employeeId], id: employeeId });
    getData(employeeId, startTime, endTime, "dropdown");
    getLeadsCharts(startTime, endTime, employeeId, "reset");
    getDisqualifiedLeadReason(startTime, endTime, employeeId, "reset");
  };

  const handleReset = () => {
    const user = getLoggedInUser();
    const startTime = dayjs().startOf("day").unix();
    const endTime = dayjs().endOf("day").unix();
    setSelectedEmployee({});
    setDates(null);
    getData(user.empId, startTime, endTime, "reset");
    getLeadsCharts(startTime, endTime, user?.empId, "reset");
    getDisqualifiedLeadReason(startTime, endTime, user?.empId, "reset");
  };

  const handleDateChange = (dates) => {
    if (!dates) return;
    const startTime = dates.startOf("month").unix();
    const endTime = dates.endOf("month").unix();
    setDates(dates);
    // getData(apiParams.employeeId, startTime, endTime, "init");
    // getLeadsCharts(startTime, endTime, apiParams.employeeId, "init");
    getDisqualifiedLeadReason(startTime, endTime, apiParams.employeeId, "init");
  };

  const getTeamEmployeeData = async (employedId) => {
    try {
      const user = getLoggedInUser();
      const res = await axiosPrivate.get(`om/employee?id=${employedId ? employedId : user?.empId}`);
      if (res.status === HTTP_STATUS.OK && res.data.status === HTTP_STATUS_MESSAGE.OK) {
        const transformed = {};
        Object.entries(res.data.data || {}).forEach(([id, name]) => {
          transformed[id] = { name, self: false };
        });

        transformed[user.empId] = {
          name: `${user.employeeName} (self)`,
          self: true,
        };

        setTeamEmployeeList(transformed);
        return transformed;
      } else {
        setTeamEmployeeList({});
        return {};
      }
    } catch {
      setTeamEmployeeList({});
      return {};
    }
  };

  useEffect(() => {
    const user = getLoggedInUser();
    const params = Object.fromEntries([...searchParams]);
    const employeeId = params?.employeeId || user?.empId;
    const startTime = params?.startTime
      ? parseInt(params?.startTime)
      : dayjs().startOf("day").unix();
    const endTime = params?.endTime ? parseInt(params?.endTime) : dayjs().endOf("day").unix();
    setApiParams({ startTime, endTime, employeeId });
    setDates([dayjs.unix(startTime), dayjs.unix(endTime)]);
    getData(employeeId, startTime, endTime, "init");
    getLeadsCharts(null, null, employeeId, "init");
    getDisqualifiedLeadReason(null, null, employeeId, "init");
  }, []);

  const getData = async (employeeId, startTime, endTime, source) => {
    const teamEmployee = await getTeamEmployeeData(employeeId);
    const forTeams = calculateForTeams({
      teamEmployee,
      employeeId,
      selectionSource: source,
    });
    const dt = teamEmployee;
    getDashboardData({
      empId: employeeId,
      startTime: startTime,
      endTime: endTime,
      forTeams,
    });
    setApiParams({ startTime, endTime, employeeId, forTeams });
    setSearchParams({
      employeeId,
      startTime: startTime.toString(),
      endTime: endTime.toString(),
      forTeams: forTeams.toString(),
    });
  };
  const leadColors = {
    Cold: "#F7A829",
    Warm: "#2EBDC5",
    Hot: "#28e175ff",
    Onhold: "#95a5a6",
    Win: "#087b1dff",
    Fresh: "#3378edff",
    Marketing: "#f38131ff",
    Disqualified: "#747373ff",
  };

  const getDashboardData = async ({ empId, startTime, endTime, forTeams }) => {
    setLoading(true);
    try {
      const res = await axiosPrivate.post("om/dashboard", {
        teamEmployeeId: empId,
        startTime,
        endTime,
        forTeams,
      });

      if (res.status === HTTP_STATUS.OK && res.data.status === HTTP_STATUS_MESSAGE.OK) {
        setLeadsData(convertLeadData(res.data.data.lead));
        setActivityData(convertTaskData(res.data.data.tasks));
        const leadData = res?.data?.data?.lead;
        const filtered = Object.fromEntries(
          Object.entries(leadData).filter(([key]) => key !== "Disqualified" && key !== "Marketing")
        );
        const labels = Object.keys(filtered);
        const colors = labels.map((label) => leadColors[label]);
        setLeadDonutChart({
          series: Object.values(filtered),
          options: {
            chart: {
              type: "donut",
            },
            labels,
            colors,
            responsive: [
              {
                breakpoint: 480,
                options: {
                  chart: {
                    width: 250,
                  },
                  legend: {
                    position: "bottom",
                  },
                },
              },
            ],
          },
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const skeletonArray = Array.from({ length: 6 });

  const navigateToLeads = ({ status, startTime, endTime, selectedEmployee }) => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (startTime) params.set("startTime", startTime);
    if (endTime) params.set("endTime", endTime);

    // pass employee ONLY if selected
    if (selectedEmployee?.id) {
      params.set("employeeId", selectedEmployee?.id);
      params.set("employeeName", selectedEmployee?.name);
    } else {
      const user = getLoggedInUser();
      params.set("employeeId", user?.empId);
      params.set("employeeName", user?.employeeName);
    }

    navigate(`/dashboard/leads?${params.toString()}`);
  };

  const navigateToActivity = ({ status, startTime, endTime, selectedEmployee }) => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (startTime) params.set("startTime", startTime);
    if (endTime) params.set("endTime", endTime);

    // pass employee ONLY if selected
    if (selectedEmployee?.id) {
      params.set("employeeId", selectedEmployee?.id);
      params.set("employeeName", selectedEmployee?.name);
    } else {
      const user = getLoggedInUser();
      params.set("employeeId", user?.empId);
      params.set("employeeName", user?.employeeName);
    }

    navigate(`/dashboard/activity?${params.toString()}`);
  };

  const [purposeChartData, setPurposeChartData] = useState(null);
  const [chartLoader, setChartLoader] = useState(false);
  const [leadSourceChartData, setLeadSourceChartData] = useState(null);
  const [propertTypeChartData, setPropertyChartData] = useState(null);
  const getLeadsCharts = async (startTime, endTime, empId, source) => {
    const teamEmployee = await getTeamEmployeeData(empId);
    const forTeams = calculateForTeams({
      teamEmployee,
      empId,
      selectionSource: source,
    });
    setChartLoader(true);
    const options = {
      url: "om/external/leadCharts",
      method: "POST",
      data: {
        forTeams: forTeams,
        teamEmployeeId: empId,
      },
    };
    if (startTime) {
      options.data.startTime = startTime;
    }
    if (endTime) {
      options.data.endTime = endTime;
    }
    axiosPrivate(options)
      .then((response) => {
        const purpose = response.data.data.purpose;
        const categories = Object.keys(purpose);
        const values = Object.values(purpose);
        setPurposeChartData({
          series: [{ data: values }],
          options: {
            chart: { type: "bar", height: 350 },
            xaxis: { categories },
          },
        });
        const leadSource = response?.data?.data.leadSource;
        const leadcategories = Object.keys(leadSource);
        const leadvalues = Object.values(leadSource);
        setLeadSourceChartData({
          series: [{ data: leadvalues }],
          options: {
            chart: { type: "bar", height: 350 },
            xaxis: { categories: leadcategories },
          },
        });
        const propertyType = response?.data?.data?.propertyType;
        const propertyategories = Object.keys(propertyType);
        const propertyvalues = Object.values(propertyType);
        setPropertyChartData({
          series: [{ data: propertyvalues }],
          options: {
            chart: { type: "bar", height: 350 },
            xaxis: { categories: propertyategories },
          },
        });
        setChartLoader(false);
      })
      .catch((error) => {
        console.log(error);
        setChartLoader(false);
      });
  };

  const [leadDonutChart, setLeadDonutChart] = useState(null);
  const [pie, setpie] = React.useState({
    series: [44, 55, 13, 43, 22],
    options: {
      chart: {
        width: 380,
        type: "pie",
      },
      labels: ["Team A", "Team B", "Team C", "Team D", "Team E"],
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              width: 200,
            },
            legend: {
              position: "bottom",
            },
          },
        },
      ],
    },
  });

  const [taskSummaryChart, setTaskSummaryChart] = useState(null);
  const [taskSummaryChartLoader, setTaskSummaryChartLoader] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [modalData, setModalData] = useState([]);
  const [leadMapLabel, setLeadMapLabel] = useState("");

  const getDisqualifiedLeadReason = async (startTime, endTime, empId, source) => {
    const teamEmployee = await getTeamEmployeeData(empId);
    const forTeams = calculateForTeams({
      teamEmployee,
      empId,
      selectionSource: source,
    });
    const options = {
      url: "om/external/disqualified-task-summary",
      method: "POST",
      data: { forTeams: forTeams, teamEmployeeId: empId },
    };
    if (startTime) {
      options.data.startTime = startTime;
    }
    if (endTime) {
      options.data.endTime = endTime;
    }
    setTaskSummaryChartLoader(true);
    axiosPrivate(options)
      .then((response) => {
        const data = response?.data?.data;

        if (!data || typeof data !== "object") {
          console.error("Invalid data format:", data);
          return;
        }

        // 🎯 Extract keys like:
        // ['singleFollowupPlanned', 'singleFollowupCompleted', ...]
        const labels = Object.keys(data);

        // 🎯 Extract count values:
        const series = labels.map((key) => data[key]?.count ?? 0);

        // Example: Use inside ApexChart
        // setTaskSummaryChart({
        //   series,
        //   options: {
        //     chart: { type: "pie" },
        //     labels,
        //   },
        // });

        const leadsMap = {};
        labels.forEach((label) => {
          leadsMap[label] = data[label].leads || [];
        });
        // setTaskSummaryChart({
        //   series,
        //   options: {
        //     chart: {
        //       type: "pie",
        //       height: 360,
        //     },
        //     labels,
        //     tooltip: {
        //       custom: function ({ seriesIndex }) {
        //         const label = labels[seriesIndex];
        //         const employees = data[label]?.employees || [];

        //         return `
        //   <div style="padding:10px;">
        //     <strong>${label}</strong><br/>
        //     <span>Count: ${series[seriesIndex]}</span><br/>
        //     <hr/>
        //     <div>
        //       <strong>Employees:</strong><br/>
        //       ${
        //         employees.length > 0 ? employees.map((e) => `• ${e}`).join("<br/>") : "No employees"
        //       }
        //     </div>
        //   </div>
        // `;
        //       },
        //     },

        //     // FIX shrinking
        //     plotOptions: {
        //       pie: {
        //         donut: {
        //           size: "100%", // keeps chart large even when labels are long
        //         },
        //       },
        //     },

        //     // Prevent Apex from pushing labels inside chart
        //     dataLabels: {
        //       enabled: true,
        //     },

        //     // Keep large labels readable
        //     legend: {
        //       position: "right",
        //       fontSize: "10px",
        //       formatter: function (label) {
        //         return label;
        //         // return label.length > 15 ? label.slice(0, 15) + "..." : label;
        //       },
        //     },

        //     responsive: [
        //       {
        //         breakpoint: 480,
        //         options: {
        //           chart: { width: 220 },
        //           legend: { position: "bottom" },
        //         },
        //       },
        //     ],
        //   },
        // });
        setTaskSummaryChart({
          series,
          options: {
            chart: {
              type: "pie",
              events: {
                dataPointSelection: function (event, chartContext, config) {
                  const index = config.dataPointIndex;
                  const label = labels[index];
                  setLeadMapLabel(label);
                  // open modal with list of employees
                  // console.log(leadsMap[label].length);
                  setModalData(leadsMap[label] || []);
                  setOpenModal(true);
                },
              },
            },

            labels,

            legend: {
              position: "left",
              fontSize: "12px",
            },

            responsive: [
              {
                breakpoint: 600,
                options: {
                  chart: { height: 380 },
                  legend: { fontSize: "10px" },
                },
              },
            ],
          },
        });
        setTaskSummaryChartLoader(false);
      })
      .catch((error) => {
        console.log(error);
        setTaskSummaryChartLoader(false);
      });
  };

  const [openLeadModal, setOpenLeadModal] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const openLeadDetails = (leadId) => {
    setSelectedLeadId(leadId);
    setOpenLeadModal(true);
  };

  return (
    <DashboardLayout loading={loading || chartLoader}>
      <DashboardNavbar />
      <LeadActivites
        open={openLeadModal}
        onClose={() => {
          setOpenLeadModal(!openLeadModal);
        }}
        leadId={selectedLeadId}
      />
      <MDBox py={3}>
        <MDBox display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <MDBox fontWeight="bold" fontSize={24}>
            Lead
          </MDBox>

          <MDBox display="flex" gap={2} flexWrap="wrap">
            <FormControl sx={{ minWidth: 220 }}>
              <ConfigProvider
                theme={{
                  algorithm: darkMode ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
                  components: {
                    Select: {
                      colorBgContainer: darkMode ? "#1a2035" : "#fff", // input box
                      colorText: darkMode ? "#fff" : "#000",
                      colorBorder: darkMode ? "#475894" : "#d9d9d9",
                      colorPrimary: darkMode ? "#1890ff" : "#1890ff",
                      optionActiveBg: darkMode ? "#1f2937" : "#f5f5f5",
                      optionSelectedBg: darkMode ? "#1a243bff" : "#e6f4ff",
                    },
                  },
                }}
              >
                <Select
                  size="large"
                  placeholder={`${
                    selectedEmployee?.name
                      ? "Selected " + selectedEmployee?.name
                      : " Select Team Member"
                  }`}
                  value={teamMember}
                  onChange={handleTeamMemberChange}
                  options={
                    teamEmployeeList &&
                    Object.entries(teamEmployeeList).map(([id, d]) => ({
                      label: d.name,
                      value: id,
                    }))
                  }
                />
              </ConfigProvider>
            </FormControl>

            <MDButton onClick={handleReset} variant="outlined" size="small" color={"secondary"}>
              Reset
            </MDButton>
          </MDBox>
        </MDBox>

        <Grid container spacing={3} py={4}>
          {(loading ? skeletonArray : leadsData).map((item, index) => (
            <Grid key={index} item xs={12} sm={6} md={3} lg={3}>
              <MDBox
                mb={1.5}
                onClick={() => {
                  if (!loading) {
                    // navigate(`/dashboard/leads?status=${item?.title}`);
                    const params = {
                      status: item?.title,
                      startTime: apiParams?.startTime,
                      endTime: apiParams?.endTime,
                      selectedEmployee,
                    };
                    navigateToLeads(params);
                  }
                }}
              >
                <ComplexStatisticsCard
                  color={item?.color || "primary"}
                  icon={item?.icon || "leaderboard"}
                  title={item?.title || ""}
                  count={item?.count || 0}
                  percentage={{ color: "success" }}
                  loading={loading}
                />
              </MDBox>
            </Grid>
          ))}
        </Grid>
        {/* <MDBox fontWeight="bold" sx={{ fontSize: 24 }} mt={0} mb={4}>
          Activity
        </MDBox>
        <Grid container spacing={3}>
          {(loading ? skeletonArray : activityData).map((item, index) => (
            <Grid key={index} item xs={12} sm={6} md={3} lg={3}>
              <MDBox
                mb={1.5}
                onClick={() => {
                  if (!loading) {
                    if (item?.title != "Connected/Dialed") {
                      const params = {
                        status: item?.title,
                        startTime: apiParams?.startTime,
                        endTime: apiParams?.endTime,
                        selectedEmployee,
                      };
                      console.log(params);
                      navigateToActivity(params);
                    }
                  }
                }}
              >
                <ComplexStatisticsCard
                  color={item?.color || "info"}
                  icon={item?.icon || "task"}
                  title={item?.title || ""}
                  count={item?.count || 0}
                  percentage={{ color: "success" }}
                  loading={loading}
                  done={item?.done}
                />
              </MDBox>
            </Grid>
          ))}
        </Grid> */}

        {/* <MDBox display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <MDBox fontWeight="bold" fontSize={24}></MDBox>

          <MDBox display="flex" gap={2} flexWrap="wrap">
            <ConfigProvider
              theme={{
                algorithm: darkMode ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
                components: {
                  DatePicker: {
                    colorBgContainer: darkMode ? "#1a2035" : "#fff",
                    colorText: darkMode ? "#fff" : "#000",
                    colorBorder: darkMode ? "#475894" : "#d9d9d9",
                    colorBgElevated: darkMode ? "#1f2937" : "#fff",
                    colorPrimary: darkMode ? "#1890ff" : "#1890ff",
                  },
                },
              }}
            >
              <RangePicker value={dates} onChange={handleDateChange} allowClear={false} />
            </ConfigProvider>
          </MDBox>
        </MDBox> */}

        <MDBox mt={4.5}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={6}>
              <MDBox mb={3}>
                <Purpose
                  data={leadDonutChart}
                  loading={loading}
                  title={"Lead State"}
                  type="donut"
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <MDBox mb={3}>
                <MDBox mb={3}>
                  <Card sx={{ padding: "1rem" }}>
                    <MDBox
                      display="flex"
                      justifyContent="space-between"
                      flexWrap="wrap"
                      gap={2}
                      py={0}
                    >
                      <MDBox fontWeight="bold" fontSize={24}>
                        <MDTypography variant="button" fontWeight="bold" color={"text"}>
                          Disqualified Lead Reason
                        </MDTypography>
                      </MDBox>

                      <MDBox display="flex" gap={2} flexWrap="wrap">
                        <ConfigProvider
                          theme={{
                            algorithm: darkMode
                              ? antdTheme.darkAlgorithm
                              : antdTheme.defaultAlgorithm,
                            components: {
                              DatePicker: {
                                colorBgContainer: darkMode ? "#1a2035" : "#fff",
                                colorText: darkMode ? "#fff" : "#000",
                                colorBorder: darkMode ? "#475894" : "#d9d9d9",
                                colorBgElevated: darkMode ? "#1f2937" : "#fff",
                                colorPrimary: darkMode ? "#1890ff" : "#1890ff",
                              },
                            },
                          }}
                        >
                          {/* <RangePicker
                            value={dates}
                            onChange={handleDateChange}
                            allowClear={false}
                            picker="month"
                          /> */}
                          <DatePicker
                            picker="month"
                            value={dates}
                            onChange={handleDateChange}
                            allowClear={false}
                          />
                        </ConfigProvider>
                      </MDBox>
                    </MDBox>
                    {taskSummaryChartLoader && (
                      <Skeleton variant="rectangular" height={230} sx={{ borderRadius: 2 }} />
                    )}
                    {!taskSummaryChartLoader && taskSummaryChart && (
                      <ReactApexChart
                        options={taskSummaryChart?.options}
                        series={taskSummaryChart?.series}
                        type="pie"
                        height={250}
                      />
                    )}
                  </Card>
                </MDBox>
              </MDBox>
            </Grid>
            {/* <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}>
                <Card sx={{ padding: "1rem" }}>
                  <ReactApexChart
                    options={state.options}
                    series={state.series}
                    type="bar"
                    height={216}
                  />
                </Card>
              </MDBox>
            </Grid> */}
          </Grid>
        </MDBox>

        <MDBox mt={4.5}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}>
                <Purpose
                  data={purposeChartData}
                  loading={chartLoader}
                  title={"Purpose"}
                  type="bar"
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}>
                <Purpose
                  data={propertTypeChartData}
                  loading={chartLoader}
                  title={"Property Type"}
                  type="bar"
                />
              </MDBox>
            </Grid>

            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}>
                <Purpose
                  data={leadSourceChartData}
                  loading={chartLoader}
                  title={"Lead Source"}
                  type="bar"
                />
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>

        <Modal open={openModal} onClose={() => setOpenModal(false)}>
          <div
            style={{
              background: "#fff", // dialog-like background
              color: "#000", // text color
              padding: "20px",
              borderRadius: "12px", // smooth corners
              width: "800px", // medium modal
              maxHeight: "600px",
              overflowY: "auto",
              margin: "60px auto",
              boxShadow: "0 6px 30px rgba(0,0,0,0.2)", // subtle shadow
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* HEADER */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
                borderBottom: "1px solid #eee",
                paddingBottom: "8px",
              }}
            >
              <h3 style={{ margin: 0 }}>
                {leadMapLabel} : {leadMapLabel && leadMapLabel.length}
              </h3>
              <button
                onClick={() => setOpenModal(false)}
                style={{
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  fontSize: "16px",
                  fontWeight: "bold",
                }}
              >
                ×
              </button>
            </div>

            {/* GRID CONTENT */}
            {modalData.length > 0 ? (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)", // 4-column grid
                  gap: "12px",
                }}
              >
                {modalData.map((e, idx) => (
                  <div
                    key={idx}
                    style={{
                      padding: "8px",
                      borderRadius: "8px",
                      background: "#f5f5f5",
                      display: "flex",
                      flexDirection: "column",
                      fontSize: "12px", // smaller card
                      alignItems: "center",
                      textAlign: "center",
                    }}
                    // 6960b9cbc8abf921dc8d54af
                    onClick={() => openLeadDetails(e?.leadId)}
                    // onClick={() => openLeadDetails("6960b9cbc8abf921dc8d54af")}
                  >
                    <strong>{e.assignedTo}</strong>
                    <div>{e.leadName || "-"}</div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: "#999", textAlign: "center" }}>No employees found.</p>
            )}
          </div>
        </Modal>
      </MDBox>
    </DashboardLayout>
  );
};

export default dashboard;
